import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useWindowDimensions from "../useWindowDimensions";
import DropdownIcon from "../Svgs/Dropdown";

const CustomDrop = ({ options, highlightPrefectures }) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const timeoutRef = useRef(null);
  const navigate = useNavigate();
  const { width } = useWindowDimensions(true);
  const [isBig, setIsBig] = useState();
  const [isSearching, setIsSearching] = useState(false);
  const [prefectureList, setPrefectureList] = useState(options);
  const [searchValue, setSearchValue] = useState("");

  useEffect(() => {
    setIsBig(width >= 770);
  }, [width]);

  useEffect(() => {
    setPrefectureList(options);
  }, [options]);

  const handleOptionClick = (option) => {
    setSelectedOption(option);
    setIsDropdownOpen(false);
    navigate(`/prefecture-listing/${option.prefecture_name}`);
  };

  const handleMouseEnter = () => {
    clearTimeout(timeoutRef.current);
    setIsDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    setIsSearching(false);
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setIsDropdownOpen(false);
      setSearchValue("");
      setPrefectureList(options);
    }, 300);
  };

  const handleSearch = (e) => {
    const searchQuery = e.target.value.toLowerCase();
    setSearchValue(e.target.value);
    
    if (searchQuery === "") {
      setIsSearching(false);
      setPrefectureList(options);
    } else {
      setIsSearching(true);
      const sortedData = options.sort((a, b) => {
        const aStartsWithQuery = a.prefecture_name
          .toLowerCase()
          .startsWith(searchQuery);
        const bStartsWithQuery = b.prefecture_name
          .toLowerCase()
          .startsWith(searchQuery);
        if (aStartsWithQuery && !bStartsWithQuery) {
          return -1;
        } else if (!aStartsWithQuery && bStartsWithQuery) {
          return 1;
        } else {
          return 0;
        }
      });
      const filteredData = sortedData.filter((option) =>
        option.prefecture_name.toLowerCase().includes(searchQuery)
      );
      setPrefectureList(filteredData);
    }
  };

  return (
    <div
      className="relative inline-block transition-all duration-200 z-20"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Main Button */}
      <div
        className={`
          ${isDropdownOpen ? "rounded-b-none shadow-lg" : "shadow-md hover:shadow-lg"} 
          w-[100vw] md:w-[12rem] md:rounded-lg
          flex justify-center items-center gap-2
          bg-[var(--medium-sea-green)]
          text-white font-semibold h-[3.2rem] cursor-pointer 
          transition-all duration-200 ease-out
          hover:brightness-110
        `}
        onClick={() => setSelectedOption(null)}
      >
        <p className="tracking-wide">All Prefectures</p>
        <div className={`transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}>
          <DropdownIcon />
        </div>
      </div>

      {/* Dropdown Menu */}
      {isDropdownOpen && (
        <div
          ref={dropdownRef}
          className={`
            absolute top-full left-0 
            bg-[var(--medium-sea-green)]
            text-white
            shadow-lg md:rounded-lg
            ${
              isBig
                ? isSearching
                  ? "w-[220px] grid-cols-1"
                  : "w-[750px] grid-cols-4"
                : "w-full max-h-[350px] overflow-y-auto"
            } 
            grid gap-1 p-3 z-50 
            transition-all duration-200 ease-out
          `}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {/* Search Input */}
          <div className={`${isBig && !isSearching ? 'col-span-4' : 'col-span-1'} mb-3`}>
            <input
              placeholder="Search Prefecture..."
              type="text"
              value={searchValue}
              onChange={handleSearch}
              className="
                w-full px-3 py-2 rounded-lg
                bg-white/20
                border border-white/30 
                placeholder:text-green-100 text-white 
                text-sm font-medium
                outline-none focus:outline-none
                focus:border-white/60 focus:bg-white/30
                transition-all duration-200
              "
              autoComplete="off"
            />
          </div>

          {/* Prefecture Options */}
          <div className={`${isBig && !isSearching ? 'col-span-4 grid grid-cols-4 gap-2' : 'col-span-1 space-y-1'}`}>
            {prefectureList &&
              prefectureList.map((option, index) => (
                <div
                  key={index}
                  onClick={() => handleOptionClick(option)}
                  className={`
                    cursor-pointer text-sm text-center py-2 px-2
                    rounded-lg transition-all duration-150 ease-out
                    ${
                      highlightPrefectures.includes(option.prefecture_name)
                        ? `bg-white/20 text-white font-semibold hover:bg-white/30`
                        : `bg-[#0eb1a9]/60 text-green-100 hover:bg-[#0eb1a9]/80 hover:text-white`
                    }
                  `}
                >
                  <span className="font-medium">
                    {option.prefecture_name}
                  </span>
                </div>
              ))}
          </div>

          {/* No results message */}
          {prefectureList.length === 0 && isSearching && (
            <div className="col-span-full text-center py-8 text-green-100">
              <p className="font-medium">No prefectures found</p>
              <p className="text-sm opacity-75">Try a different search term</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomDrop;